import { action, makeObservable, observable, runInAction } from 'mobx';
import { paymentService } from '../../services';
import { ApiError, ClinicPaymentQuery, Payment } from '../../types';

const DEFAULT_TAKE = 10;

export class PaymentStore {
  items: Payment[] = [];
  total = 0;
  query: ClinicPaymentQuery = { skip: 0, take: DEFAULT_TAKE };
  loadingList = false;
  listError: ApiError | null = null;

  detail: Payment | null = null;
  loadingDetail = false;
  detailError: ApiError | null = null;

  downloadingInvoiceFor: string | null = null;

  constructor() {
    makeObservable(this, {
      items: observable,
      total: observable,
      query: observable,
      loadingList: observable,
      listError: observable,
      detail: observable,
      loadingDetail: observable,
      detailError: observable,
      downloadingInvoiceFor: observable,
      setQuery: action,
      loadList: action,
      loadDetail: action,
      clearDetail: action,
      downloadInvoice: action,
    });
  }

  setQuery(patch: Partial<ClinicPaymentQuery>): void {
    const next: ClinicPaymentQuery = { ...this.query, ...patch };
    if (!('skip' in patch)) next.skip = 0;
    this.query = next;
  }

  async loadList(): Promise<void> {
    this.loadingList = true;
    this.listError = null;
    try {
      const res = await paymentService.list(this.query);
      runInAction(() => {
        this.items = res.items;
        this.total = res.total;
      });
    } catch (err) {
      runInAction(() => {
        this.listError = err as ApiError;
      });
    } finally {
      runInAction(() => {
        this.loadingList = false;
      });
    }
  }

  async loadDetail(paymentId: string): Promise<void> {
    this.loadingDetail = true;
    this.detailError = null;
    this.detail = null;
    try {
      const payment = await paymentService.detail(paymentId);
      runInAction(() => {
        this.detail = payment;
      });
    } catch (err) {
      runInAction(() => {
        this.detailError = err as ApiError;
      });
    } finally {
      runInAction(() => {
        this.loadingDetail = false;
      });
    }
  }

  clearDetail(): void {
    this.detail = null;
    this.detailError = null;
  }

  async downloadInvoice(orderId: string): Promise<string> {
    this.downloadingInvoiceFor = orderId;
    try {
      return await paymentService.invoiceText(orderId);
    } finally {
      runInAction(() => {
        this.downloadingInvoiceFor = null;
      });
    }
  }
}
